import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import QuotaDashboardTabs from '../components/quota/QuotaDashboardTabs';
import QuotaAlertPanel from '../components/quota/QuotaAlertPanel';
import QuotaSummaryCards from '../components/quota/QuotaSummaryCards';
import QuotaIncreaseModal from '../components/quota/QuotaIncreaseModal';
// GPUCatalogOverview moved into QuotaDashboardTabs "Catalog" tab
import { getApiUrl } from '../utils/api';
import { QuotaData, getDataFreshness, formatLastUpdated } from '../components/quota/quotaUtils';

interface QuotaSummary {
  total_resources: number;
  resources_near_limit: number;
  resources_at_limit: number;
  by_provider: {
    [key: string]: {
      total: number;
      near_limit: number;
      at_limit: number;
    };
  };
  alerts: {
    active: number;
    unacknowledged: number;
  };
}

interface ProviderStatus {
  authenticated: boolean;
  error: string | null;
  has_data: boolean;
}

interface QuotaAlert {
  id: number;
  provider: string;
  region: string;
  instance_family: string;
  alert_type: string;
  alert_level: 'warning' | 'critical' | 'emergency';
  message: string;
  is_active: boolean;
  is_acknowledged: boolean;
  created_at: string;
}

export default function AdminGpuQuotaDashboard() {
  const navigate = useNavigate();
  const [quotas, setQuotas] = useState<QuotaData[]>([]);
  const [summary, setSummary] = useState<QuotaSummary | null>(null);
  const [alerts, setAlerts] = useState<QuotaAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedQuota, setSelectedQuota] = useState<QuotaData | null>(null);
  const [selectedGroupLabel, setSelectedGroupLabel] = useState<string | undefined>(undefined);
  const [selectedGroupFamilies, setSelectedGroupFamilies] = useState<string[] | undefined>(undefined);
  const [showIncreaseModal, setShowIncreaseModal] = useState(false);
  const [providerStatus, setProviderStatus] = useState<{ [key: string]: ProviderStatus }>({});

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      fetchQuotaData();
    }, 60_000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Initial fetch + 60s auto-poll with visibility pause/resume
  useEffect(() => {
    fetchQuotaData();
    startPolling();

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchQuotaData();
        startPolling();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuotaData = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('authToken');

      if (!token) {
        navigate('/login');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Set loading to false early to show partial data
      setLoading(false);

      // Fetch each endpoint independently with timeout
      const fetchWithTimeout = (url: string, timeoutMs: number = 15000) => {
        return Promise.race([
          axios.get(url, { headers }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };

      // Fetch quota overview — keep existing data on failure
      fetchWithTimeout(`${getApiUrl()}/api/quotas/overview`)
        .then((response: any) => {
          setQuotas(response.data || []);
        })
        .catch((err) => {
          console.warn('Failed to fetch quota overview:', err);
        });

      // Fetch summary — keep existing data on failure
      fetchWithTimeout(`${getApiUrl()}/api/quotas/summary`)
        .then((response: any) => {
          setSummary(response.data);
        })
        .catch((err) => {
          console.warn('Failed to fetch summary:', err);
        });

      // Fetch alerts — keep existing data on failure
      fetchWithTimeout(`${getApiUrl()}/api/quotas/alerts/active`)
        .then((response: any) => {
          setAlerts(response.data.filter((a: QuotaAlert) => !a.is_acknowledged));
        })
        .catch((err) => {
          console.warn('Failed to fetch alerts:', err);
        });

      // Fetch status — keep existing data on failure
      fetchWithTimeout(`${getApiUrl()}/api/quotas/status`)
        .then((response: any) => {
          setProviderStatus(response.data.providers || {});
        })
        .catch((err) => {
          console.warn('Failed to fetch status:', err);
        });

    } catch (err: any) {
      console.error('Failed to initialize quota dashboard:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view quota data');
      }
      setLoading(false);
    }
  };

  const handleRefresh = async (provider?: string) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('authToken');

      const params = provider && provider !== 'all' ? { provider } : {};

      await axios.post(
        `${getApiUrl()}/api/quotas/refresh`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params
        }
      );

      // Background task hits AWS Service Quotas API across all regions — takes 5-10s.
      // Re-fetch twice: early (catches fast updates) and late (catches slow ones).
      setTimeout(() => fetchQuotaData(), 3000);
      setTimeout(() => {
        fetchQuotaData();
        setRefreshing(false);
      }, 8000);
    } catch (err) {
      console.error('Failed to refresh quotas:', err);
      setRefreshing(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId: number) => {
    try {
      const token = localStorage.getItem('authToken');

      await axios.post(
        `${getApiUrl()}/api/quotas/alerts/${alertId}/acknowledge`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Remove acknowledged alert from list
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleRequestIncrease = (quota: QuotaData, groupLabel?: string, groupFamilies?: string[]) => {
    setSelectedQuota(quota);
    setSelectedGroupLabel(groupLabel);
    setSelectedGroupFamilies(groupFamilies);
    setShowIncreaseModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Loading quota data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="text-red-400">{error}</div>
        </div>
      </div>
    );
  }

  // Calculate credential warning level
  const getCredentialWarning = () => {
    const providers = Object.keys(providerStatus);
    const authenticated = providers.filter(p => providerStatus[p]?.authenticated).length;
    const total = providers.length;

    // No data yet (status endpoint still loading or timed out) — don't show error
    if (total === 0) return null;

    if (authenticated === 0) {
      return { level: 'error', message: 'No cloud provider credentials configured' };
    } else if (authenticated < total) {
      const missing = providers.filter(p => !providerStatus[p]?.authenticated).join(', ');
      return { level: 'warning', message: `Some providers missing credentials: ${missing.toUpperCase()}` };
    }
    return null;
  };

  const credentialWarning = getCredentialWarning();

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Credential Status Banner */}
        {credentialWarning && (
          <div className={`mb-6 p-4 rounded-lg border ${
            credentialWarning.level === 'error'
              ? 'bg-red-900/20 border-red-800 text-red-400'
              : 'bg-yellow-900/20 border-yellow-800 text-yellow-400'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium">{credentialWarning.message}</p>
                  <p className="text-sm mt-1 opacity-80">
                    Configure credentials to see real-time quota data.
                    <button type="button" className="underline hover:opacity-100">View documentation</button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">GPU Quota Dashboard</h1>
            <p className="text-gray-400">
              Monitor and manage GPU quotas across all cloud providers
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {quotas.length > 0 && (() => {
              const oldest = quotas.reduce((o, q) =>
                new Date(q.last_updated) < new Date(o.last_updated) ? q : o
              );
              const freshness = getDataFreshness(oldest.last_updated);
              return (
                <span className={`text-xs px-2.5 py-1 rounded-full border ${freshness.bgColor} ${freshness.color}`}>
                  {formatLastUpdated(oldest.last_updated)}
                </span>
              );
            })()}
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Back to Admin
            </button>
            <button
              onClick={() => handleRefresh()}
              disabled={refreshing}
              className={`px-4 py-2 ${
                refreshing
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg text-sm font-medium transition-colors flex items-center`}
            >
              {refreshing && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {refreshing ? 'Refreshing...' : 'Refresh All'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && <QuotaSummaryCards summary={summary} />}

        {/* Alerts Panel */}
        {alerts.length > 0 && (
          <div className="mb-6">
            <QuotaAlertPanel
              alerts={alerts}
              onAcknowledge={handleAcknowledgeAlert}
            />
          </div>
        )}

        {/* Main Dashboard with Tabs */}
        <QuotaDashboardTabs
          quotas={quotas}
          onRefresh={handleRefresh}
          onRequestIncrease={handleRequestIncrease}
          providerStatus={providerStatus}
        />

        {/* Quota Increase Modal */}
        {showIncreaseModal && selectedQuota && (
          <QuotaIncreaseModal
            quota={selectedQuota}
            groupLabel={selectedGroupLabel}
            groupFamilies={selectedGroupFamilies}
            onClose={() => setShowIncreaseModal(false)}
            onSubmit={() => {
              setShowIncreaseModal(false);
              // Refresh data after submission
              setTimeout(() => handleRefresh(selectedQuota.provider), 1000);
            }}
          />
        )}
      </div>
    </div>
  );
}